import { Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { RenovationItemInput } from '@/components/forms/RenovationItemInput';
import {
  type RenovationOption,
  RenovationOptionsEditor,
} from '@/components/forms/RenovationOptionsEditor';
import { RENOVATION_SUMMARY_SUGGESTIONS } from '@/lib/constants';
import type { CreateListingInput, RenovationItem } from '@/types/listing';

interface ListingStep2Props {
  data: Partial<CreateListingInput>;
  onChange: (updates: Partial<CreateListingInput>) => void;
  entityId: string;
}

export function ListingStep2({ data, onChange, entityId }: ListingStep2Props) {
  // Auto-calculate All-In Purchase Price Range when dependencies change
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally omit renovationPrice values to avoid infinite loops - we only want to recalculate when source values change
  useEffect(() => {
    const housePrice = data.housePrice || 0;
    const scopeMin = data.renovationScopeMin || 0;
    const scopeMax = data.renovationScopeMax || 0;

    // Only calculate if at least one scope value is greater than 0
    const hasValidScope = scopeMin > 0 || scopeMax > 0;

    if (hasValidScope && housePrice > 0) {
      const newPriceMin = housePrice + scopeMin;
      const newPriceMax = housePrice + scopeMax;

      // Only update if values actually changed to avoid infinite loops
      if (newPriceMin !== data.renovationPriceMin || newPriceMax !== data.renovationPriceMax) {
        onChange({
          renovationPriceMin: newPriceMin,
          renovationPriceMax: newPriceMax,
        });
      }
    } else if (!hasValidScope && (data.renovationPriceMin || data.renovationPriceMax)) {
      // Clear the values if scope is removed/zero (use null to explicitly remove from DB)
      onChange({
        renovationPriceMin: null,
        renovationPriceMax: null,
      });
    }
  }, [data.housePrice, data.renovationScopeMin, data.renovationScopeMax]);

  const handleAddRenovationItem = useCallback(() => {
    const currentItems = data.renovationItems || [];
    const newItem: RenovationItem = { item: '', isFeatured: false };
    onChange({
      renovationItems: [...currentItems, newItem],
    });
  }, [data.renovationItems, onChange]);

  const handleUpdateRenovationItem = useCallback(
    (index: number, updates: Partial<RenovationItem>) => {
      const currentItems = data.renovationItems || [];
      const newItems = [...currentItems];
      const current = newItems[index];
      if (current) {
        const updated: RenovationItem = {
          item: updates.item !== undefined ? updates.item : current.item,
          isFeatured: updates.isFeatured !== undefined ? updates.isFeatured : current.isFeatured,
        };
        newItems[index] = updated;
        onChange({ renovationItems: newItems });
      }
    },
    [data.renovationItems, onChange]
  );

  const handleRemoveRenovationItem = useCallback(
    (index: number) => {
      const currentItems = data.renovationItems || [];
      const newItems = [...currentItems];
      newItems.splice(index, 1);
      onChange({ renovationItems: newItems });
    },
    [data.renovationItems, onChange]
  );

  return (
    <div className="space-y-[24px]">
      {/* Pricing */}
      <div className="rounded-[12px] border border-[#e5e5e5] bg-white p-[24px]">
        <h3 className="mb-[16px] text-[16px] font-semibold text-[#181818]">Pricing Information</h3>

        <div className="space-y-[16px]">
          {/* Home Purchase Price (single value) */}
          <label className="block">
            <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
              Home Purchase Price ($)
            </span>
            <input
              type="number"
              min="0"
              value={data.housePrice || ''}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                onChange({ housePrice: value > 0 ? value : undefined });
              }}
              className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
              placeholder="250000"
            />
          </label>

          {/* Renovation Scope Range */}
          <div>
            <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
              Renovation Scope Range ($)
            </span>
            <div className="grid grid-cols-2 gap-[12px]">
              <label className="block">
                <span className="mb-[4px] block text-[11px] text-[#8d8c8f]">Min</span>
                <input
                  type="number"
                  min="0"
                  value={data.renovationScopeMin || ''}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    onChange({ renovationScopeMin: value > 0 ? value : null });
                  }}
                  className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
                  placeholder="50000"
                />
              </label>
              <label className="block">
                <span className="mb-[4px] block text-[11px] text-[#8d8c8f]">Max</span>
                <input
                  type="number"
                  min="0"
                  value={data.renovationScopeMax || ''}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    onChange({ renovationScopeMax: value > 0 ? value : null });
                  }}
                  className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
                  placeholder="75000"
                />
              </label>
            </div>
          </div>

          {/* All-In Purchase Price Range (auto-calculated) */}
          <div>
            <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
              All-In Purchase Price Range ($)
              <span className="ml-[8px] text-[11px] font-normal text-[#8d8c8f]">
                (Auto-calculated: Home Purchase Price + Renovation Scope)
              </span>
            </span>
            <div className="grid grid-cols-2 gap-[12px]">
              <label className="block">
                <span className="mb-[4px] block text-[11px] text-[#8d8c8f]">Min</span>
                <input
                  type="number"
                  value={data.renovationPriceMin || ''}
                  readOnly
                  className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-[#f5f5f5] px-[12px] text-[13px] text-[#646464] cursor-not-allowed"
                  placeholder="300000"
                />
              </label>
              <label className="block">
                <span className="mb-[4px] block text-[11px] text-[#8d8c8f]">Max</span>
                <input
                  type="number"
                  value={data.renovationPriceMax || ''}
                  readOnly
                  className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-[#f5f5f5] px-[12px] text-[13px] text-[#646464] cursor-not-allowed"
                  placeholder="350000"
                />
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Renovation Summary */}
      <div className="rounded-[12px] border border-[#e5e5e5] bg-white p-[24px]">
        <h3 className="mb-[16px] text-[16px] font-semibold text-[#181818]">Renovation Summary</h3>

        <div className="space-y-[16px]">
          {/* Renovation Items List */}
          <div>
            <div className="mb-[12px] flex items-center justify-end">
              <button
                type="button"
                onClick={handleAddRenovationItem}
                className="flex items-center gap-[4px] rounded-[6px] bg-[#055135] px-[12px] py-[6px] text-[12px] font-medium text-white transition-colors hover:bg-[#044128]"
              >
                <Plus size={14} />
                Add Item
              </button>
            </div>

            {!data.renovationItems || data.renovationItems.length === 0 ? (
              <div className="rounded-[8px] border-2 border-dashed border-[#e5e5e5] p-[16px] text-center">
                <p className="text-[13px] text-[#8d8c8f]">No renovation items added</p>
              </div>
            ) : (
              <div className="space-y-[8px]">
                {data.renovationItems.map((item, index) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: Using index as key is intentional - value-based keys cause input to remount on keystroke
                    key={index}
                    className="flex items-center gap-[12px] rounded-[8px] border border-[#e5e5e5] bg-[#fafafa] p-[12px]"
                  >
                    <RenovationItemInput
                      value={item.item}
                      onChange={(val) => handleUpdateRenovationItem(index, { item: val })}
                      suggestions={RENOVATION_SUMMARY_SUGGESTIONS}
                      ariaLabel={`Renovation item ${index + 1}`}
                    />
                    <label className="flex items-center gap-[6px] whitespace-nowrap text-[12px] text-[#646464]">
                      <input
                        type="checkbox"
                        checked={item.isFeatured}
                        onChange={(e) =>
                          handleUpdateRenovationItem(index, { isFeatured: e.target.checked })
                        }
                        className="h-[16px] w-[16px] rounded border-[#e5e5e5] text-[#055135] focus:ring-[#055135]"
                      />
                      Featured
                    </label>
                    <button
                      type="button"
                      onClick={() => handleRemoveRenovationItem(index)}
                      className="flex h-[28px] w-[28px] items-center justify-center rounded-[6px] text-[#646464] transition-colors hover:bg-[#fee] hover:text-[#d4183d]"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label className="block">
            <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
              Renovation Details
            </span>
            <textarea
              value={data.detailedRenovationList || ''}
              onChange={(e) => onChange({ detailedRenovationList: e.target.value })}
              rows={4}
              className="w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] py-[8px] text-[13px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
              placeholder="Detailed breakdown of all renovation work..."
            />
          </label>
        </div>
      </div>

      {/* Renovation Options (Multiple Design Choices) */}
      <div className="rounded-[12px] border border-[#e5e5e5] bg-white p-[24px]">
        <h3 className="mb-[16px] text-[16px] font-semibold text-[#181818]">Renovation Options</h3>
        <p className="mb-[16px] text-[13px] text-[#8d8c8f]">
          Add multiple renovation design options with different costs and styles
        </p>
        <RenovationOptionsEditor
          value={(data.renovationOptions as RenovationOption[]) || []}
          onChange={(renovationOptions) => onChange({ renovationOptions })}
          entityId={entityId}
          label=""
          maxOptions={5}
        />
      </div>
    </div>
  );
}
